import { ForRentByType } from '@zg-rentals/ts-rental-manager-api-client';
import { FlowType } from '../components/CreateListingPage/types';

const APPLICATIONS_ALLOWED_BY_FOR_RENT_BY_KEY: Record<ForRentByType, boolean> = {
  [ForRentByType.OWNER]: true,
  [ForRentByType.OWNER_RESIDENT]: true,
  [ForRentByType.TENANT]: false,
  [ForRentByType.AGENT]: true,
};

export type HasContentObjType = {
  roommateTotalCount?: boolean;
  roommateCount?: boolean;
  roommateDetailsDescription?: boolean;
  renterAgeAndGenderPreferred?: boolean;
};

type RoomForRentData = {
  currentRoommatesDetails?: {
    roommateFemaleCount?: string;
    roommateMaleCount?: string;
    roommateNonBinaryCount?: string;
    roommatePreferNotToIdentifyCount?: string;
    roommateTotalCount?: string;
    roommateDetailsDescription?: string;
  };
  roommateRequirements?: {
    roommateGenderPreferences?: Array<string>;
    roommateMinAgePreference?: number | null;
  };
};

/**
 * Determines which room-for-rent flow type a persona belongs to
 * - RYAN_FLOW: Owner-Resident or Tenant (detailed roommate info)
 * - IRENE_FLOW: Owner or Agent (basic roommate count only)
 */
export function getRoomForRentFlowObj(forRentBy?: ForRentByType): {
  isRyanFlow: boolean;
  isIreneFlow: boolean;
} {
  const isRyanFlow = forRentBy === ForRentByType.OWNER_RESIDENT || forRentBy === ForRentByType.TENANT;
  const isIreneFlow = forRentBy === ForRentByType.OWNER || forRentBy === ForRentByType.AGENT;

  return { isRyanFlow, isIreneFlow };
}

/**
 * Returns the room-for-rent flow type for a given persona
 */
export function getRoomForRentFlow(forRentBy?: ForRentByType): string | null {
  const { isRyanFlow, isIreneFlow } = getRoomForRentFlowObj(forRentBy);

  if (isRyanFlow) {
    return FlowType.ROOM_FOR_RENT_RYAN;
  }

  if (isIreneFlow) {
    return FlowType.ROOM_FOR_RENT_IRENE;
  }

  return null;
}

/**
 * Checks if changing forRentBy will change the applications acceptance status
 */
export function checkIsApplicationAcceptedValueChanging({
  isApplicationsAccepted,
  nextForRentByVal,
}: {
  isApplicationsAccepted: boolean;
  nextForRentByVal: ForRentByType;
}): {
  isApplicationAcceptedValueChanging: boolean;
  isApplicationsAcceptedChangingToTrueValue: boolean;
  nextIsApplicationsAccepted: boolean;
} {
  const nextIsApplicationsAccepted = APPLICATIONS_ALLOWED_BY_FOR_RENT_BY_KEY[nextForRentByVal];
  const isApplicationAcceptedValueChanging = isApplicationsAccepted !== nextIsApplicationsAccepted;
  const isApplicationsAcceptedChangingToTrueValue =
    isApplicationAcceptedValueChanging && nextIsApplicationsAccepted === true;

  return {
    isApplicationAcceptedValueChanging,
    isApplicationsAcceptedChangingToTrueValue,
    nextIsApplicationsAccepted,
  };
}

/**
 * Checks if switching room-for-rent flow will result in data loss
 * Returns which fields have content and whether a warning should display
 *
 * @param roomForRentFlow - Current flow (RYAN_FLOW or IRENE_FLOW)
 * @param roomForRent - Current room-for-rent data
 */
export function checkDisplayForRentByChangeWarning({
  roomForRentFlow,
  roomForRent,
}: {
  roomForRentFlow: string | null;
  roomForRent: RoomForRentData;
}): {
  displayWarning: boolean;
  hasContentObj: HasContentObjType;
} {
  const currentRoommatesDetails = roomForRent.currentRoommatesDetails ?? {};
  const roommateRequirements = roomForRent.roommateRequirements ?? {};

  const {
    roommateFemaleCount = '',
    roommateMaleCount = '',
    roommateNonBinaryCount = '',
    roommatePreferNotToIdentifyCount = '',
    roommateTotalCount = '',
    roommateDetailsDescription = '',
  } = currentRoommatesDetails;

  const { roommateGenderPreferences = [], roommateMinAgePreference = null } = roommateRequirements;

  let displayWarning = false;
  const hasContentObj: HasContentObjType = {};

  if (roomForRentFlow === FlowType.ROOM_FOR_RENT_IRENE) {
    if (roommateTotalCount !== '') {
      displayWarning = true;
      hasContentObj.roommateTotalCount = true;
    }
  }

  if (roomForRentFlow === FlowType.ROOM_FOR_RENT_RYAN) {
    const hasRoommateCountContent =
      roommateFemaleCount !== '' ||
      roommateMaleCount !== '' ||
      roommateNonBinaryCount !== '' ||
      roommatePreferNotToIdentifyCount !== '';

    const hasRoommateMinAgeAndGenderPreferenceContent =
      roommateGenderPreferences.length > 0 || roommateMinAgePreference !== null;

    if (hasRoommateCountContent) {
      displayWarning = true;
      hasContentObj.roommateCount = true;
    }

    if (roommateDetailsDescription !== '') {
      displayWarning = true;
      hasContentObj.roommateDetailsDescription = true;
    }

    if (hasRoommateMinAgeAndGenderPreferenceContent) {
      displayWarning = true;
      hasContentObj.renterAgeAndGenderPreferred = true;
    }
  }

  return {
    displayWarning,
    hasContentObj,
  };
}
